import { useState, useCallback, useEffect } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import { Helmet } from 'react-helmet-async'
import {
    MagnifyingGlassIcon,
    FunnelIcon,
    AcademicCapIcon,
    MapPinIcon,
    CheckBadgeIcon,
    ArrowRightIcon,
    StarIcon,
    UsersIcon,
    CalendarIcon,
    BuildingOfficeIcon,
    CurrencyRupeeIcon,
    XMarkIcon,
    AdjustmentsHorizontalIcon
} from '@heroicons/react/24/outline'
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid'
import { universityAPI, approvalAPI, degreeAPI, specializationAPI } from '../../services/api'
import LoadingSpinner from '../../components/Common/LoadingSpinner'
import ErrorMessage from '../../components/Common/ErrorMessage'
import EnquiryModal from '../../components/Common/EnquiryModal'

const Universities = () => {
    const [filters, setFilters] = useState({
        search: '',
        level: '',
        degrees: [],
        specializations: [],
        approvals: [],
        feeRange: '',
        page: 1,
        limit: 12,
        sort: 'order'
    })
    const [searchParams] = useSearchParams()

    // Sync filters with URL params
    useEffect(() => {
        const level = searchParams.get('level') || ''
        const degrees = searchParams.get('degrees') ? searchParams.get('degrees').split(',') : []
        const specializations = searchParams.get('specializations') ? searchParams.get('specializations').split(',') : []
        const approvals = searchParams.get('approvals') ? searchParams.get('approvals').split(',') : []
        const feeRange = searchParams.get('feeRange') || ''
        const search = searchParams.get('search') || ''

        setFilters(prev => ({
            ...prev,
            level,
            degrees,
            specializations,
            approvals,
            feeRange,
            search,
            page: 1
        }))
    }, [searchParams])

    const [sidebarOpen, setSidebarOpen] = useState(false)
    const [enquiryModal, setEnquiryModal] = useState({
        isOpen: false,
        university: null,
        type: ''
    })

    const { data, isLoading, error, refetch } = useQuery({
        queryKey: ['universities', filters],
        queryFn: () => {
            // Convert arrays to comma-separated strings for API
            const apiFilters = {
                ...filters,
                degrees: filters.degrees.length > 0 ? filters.degrees.join(',') : undefined,
                specializations: filters.specializations.length > 0 ? filters.specializations.join(',') : undefined,
                approvals: filters.approvals.length > 0 ? filters.approvals.join(',') : undefined,
            };
            // Remove empty arrays
            Object.keys(apiFilters).forEach(key => {
                if (apiFilters[key] === undefined || apiFilters[key] === '') {
                    delete apiFilters[key];
                }
            });
            return universityAPI.getUniversities(apiFilters);
        },
        keepPreviousData: false,
    })

    // Fetch master data for filters
    const { data: degreesData, isLoading: degreesLoading, error: degreesError } = useQuery({
        queryKey: ['degrees'],
        queryFn: () => degreeAPI.getDegrees()
    })

    // Fetch specializations based on selected degrees
    const { data: specializationsData, isLoading: specializationsLoading, error: specializationsError } = useQuery({
        queryKey: ['specializations', filters.degrees],
        queryFn: () => {
            if (filters.degrees.length > 0) {
                // Fetch specializations for selected degrees
                return Promise.all(
                    filters.degrees.map(degreeId =>
                        specializationAPI.getSpecializations({ degree: degreeId })
                    )
                ).then(results => {
                    // Flatten and deduplicate specializations
                    const allSpecializations = results.flatMap(result => result.data?.specializations || []);
                    const uniqueSpecializations = allSpecializations.filter((spec, index, self) =>
                        index === self.findIndex(s => s._id === spec._id)
                    );
                    return { data: { specializations: uniqueSpecializations } };
                });
            } else {
                // Fetch all specializations when no degrees selected
                return specializationAPI.getSpecializations();
            }
        },
        enabled: true
    })

    const { data: approvalsData, isLoading: approvalsLoading, error: approvalsError } = useQuery({
        queryKey: ['approvals'],
        queryFn: () => approvalAPI.getApprovals()
    })

    const degrees = (degreesData?.data?.degrees || []).filter(degree =>
        !filters.level || degree.level === filters.level
    )
    const specializations = specializationsData?.data?.specializations || []
    const approvals = approvalsData?.data?.approvals || []


    const handleFilterChange = (key, value) => {
        setFilters(prev => {
            const newFilters = {
                ...prev,
                [key]: value,
                page: 1 // Reset to first page when filters change
            };

            // If level changes, reset degrees and specializations
            if (key === 'level') {
                newFilters.degrees = [];
                newFilters.specializations = [];
            }

            return newFilters;
        })
    }


    const handleMultiSelectFilter = (key, value, checked) => {
        setFilters(prev => {
            const newFilters = {
                ...prev,
                [key]: checked
                    ? [...prev[key], value]
                    : prev[key].filter(item => item !== value),
                page: 1
            };

            // If degrees are being changed, clear specializations
            if (key === 'degrees') {
                newFilters.specializations = [];
            }

            return newFilters;
        })
    }

    const clearAllFilters = () => {
        setFilters({
            search: '',
            level: '',
            degrees: [],
            specializations: [],
            approvals: [],
            feeRange: '',
            page: 1,
            limit: 12,
            sort: 'order'
        })
    }

    const handlePageChange = (newPage) => {
        setFilters(prev => ({ ...prev, page: newPage }))
    }

    const handleEnquiry = (university, type) => {
        setEnquiryModal({
            isOpen: true,
            university,
            type
        })
    }

    const handleCloseEnquiryModal = () => {
        setEnquiryModal({
            isOpen: false,
            university: null,
            type: ''
        })
    }

    // University Image Component with fallback
    const UniversityImage = ({ university }) => {
        const [imageError, setImageError] = useState(false)
        const [logoError, setLogoError] = useState(false)

        const handleImageError = useCallback(() => {
            setImageError(true)
        }, [])

        const handleLogoError = useCallback(() => {
            setLogoError(true)
        }, [])

        // Fallback to logo
        if (university.logo?.url && !logoError) {
            return (
                <div className="w-full h-full bg-gradient-to-br from-primary-600 to-primary-700 flex items-center justify-center">
                    <img
                        src={university.logo.url}
                        alt={university.name}
                        className="object-contain"
                        onError={handleLogoError}
                    />
                </div>
            )
        }

        // Final fallback - show initial
        return (
            <div className="w-full h-full bg-gradient-to-br from-primary-600 to-primary-700 flex items-center justify-center">
                <div className="text-white text-center">
                    <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-2">
                        <span className="text-2xl font-bold">{university.name.charAt(0)}</span>
                    </div>
                    <div className="text-sm opacity-90">University</div>
                </div>
            </div>
        )
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <ErrorMessage message="Failed to load universities" onRetry={refetch} />
            </div>
        )
    }

    const universities = data?.data?.universities || []
    const pagination = data?.data?.pagination || {}

    return (
        <>
            <Helmet>
                <title>Universities & Colleges | Margdarshak</title>
                <meta name="description" content="Browse top universities and colleges offering online and distance learning programs. Find UGC recognized institutions with quality education." />
                <meta name="keywords" content="universities, colleges, online education, distance learning, UGC recognized, higher education" />
            </Helmet>

            <div className="min-h-screen bg-gray-50 pt-20">
                {/* Modern Hero Section */}
                <section className="relative overflow-hidden bg-white py-16 lg:py-24 border-b border-gray-100">
                    {/* Subtle Grid Pattern */}
                    <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(#000 1px, transparent 1px)', backgroundSize: '32px 32px' }}></div>

                    {/* Decorative Elements */}
                    <div className="absolute top-0 right-0 -mt-20 -mr-20 w-80 h-80 bg-primary-50 rounded-full blur-3xl opacity-50"></div>
                    <div className="absolute bottom-0 left-0 -mb-20 -ml-20 w-72 h-72 bg-accent-50 rounded-full blur-3xl opacity-50"></div>

                    <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                        <div className="max-w-4xl mx-auto text-center">
                            <motion.div
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.5 }}
                                className="inline-flex items-center space-x-2 bg-primary-50 text-primary-700 px-4 py-2 rounded-full text-sm font-bold uppercase tracking-widest mb-8 border border-primary-100"
                            >
                                <BuildingOfficeIcon className="w-5 h-5" />
                                <span>Discover Excellence</span>
                            </motion.div>

                            <motion.h1
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.1 }}
                                className="text-4xl sm:text-5xl lg:text-6xl font-black text-gray-900 mb-6 leading-tight tracking-tight"
                            >
                                Top <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent-500 to-accent-600">Universities</span> &{' '}
                                <span className="text-primary-600">Colleges</span>
                            </motion.h1>

                            <motion.p
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.2 }}
                                className="text-lg sm:text-xl text-gray-600 mb-10 max-w-2xl mx-auto leading-relaxed"
                            >
                                Explore India's leading institutions offering world-class online and distance learning programs, tailored for your career growth.
                            </motion.p>
                        </div>
                    </div>
                </section>

                {/* Search and Sort Section */}
                <section className="py-6 bg-white border-b border-gray-200">
                    <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="flex flex-col lg:flex-row gap-4 items-center">
                            {/* Mobile Filter Toggle */}
                            <button
                                onClick={() => setSidebarOpen(!sidebarOpen)}
                                className="lg:hidden flex items-center space-x-2 bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors"
                            >
                                <AdjustmentsHorizontalIcon className="w-5 h-5" />
                                <span>Filters</span>
                            </button>

                            {/* Search */}
                            <div className="flex-1">
                                <div className="relative">
                                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                                    <input
                                        type="text"
                                        placeholder="Search universities..."
                                        value={filters.search}
                                        onChange={(e) => handleFilterChange('search', e.target.value)}
                                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
                                    />
                                </div>
                            </div>

                            {/* Sort - Removed as per user request */}
                        </div>
                    </div>
                </section>

                {/* Results Section */}
                <section className="py-12">
                    <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="flex flex-col lg:flex-row gap-8">
                            {/* Sidebar Filters */}
                            <div className={`lg:w-80 ${sidebarOpen ? 'block' : 'hidden lg:block'}`}>
                                <div className="bg-white rounded-xl shadow-soft p-6 sticky top-24">
                                    <div className="flex items-center justify-between mb-6">
                                        <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
                                        <div className="flex items-center space-x-2">
                                            <button
                                                onClick={clearAllFilters}
                                                className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                                            >
                                                Clear All
                                            </button>
                                            <button
                                                onClick={() => setSidebarOpen(false)}
                                                className="lg:hidden p-1 hover:bg-gray-100 rounded"
                                            >
                                                <XMarkIcon className="w-5 h-5 text-gray-500" />
                                            </button>
                                        </div>
                                    </div>

                                    {/* Level Filter */}
                                    <div className="mb-6">
                                        <label className="block text-sm font-medium text-gray-700 mb-3">Level</label>
                                        <select
                                            value={filters.level}
                                            onChange={(e) => handleFilterChange('level', e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                        >
                                            <option value="">All Levels</option>
                                            <option value="Undergraduate (UG)">Undergraduate</option>
                                            <option value="Postgraduate (PG)">Postgraduate</option>
                                            <option value="Diploma">Diploma</option>
                                            <option value="Certificate">Certificate</option>
                                        </select>
                                    </div>

                                    {/* Degrees Filter */}
                                    <div className="mb-6">
                                        <label className="block text-sm font-medium text-gray-700 mb-3">Degrees</label>
                                        {degreesLoading ? (
                                            <div className="flex items-center justify-center py-4">
                                                <LoadingSpinner size="sm" />
                                            </div>
                                        ) : degreesError ? (
                                            <div className="text-red-500 text-sm">Failed to load degrees</div>
                                        ) : (
                                            <div className="max-h-48 overflow-y-auto space-y-2">
                                                {degrees.map((degree) => (
                                                    <label key={degree._id} className="flex items-center space-x-2 cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            checked={filters.degrees.includes(degree._id)}
                                                            onChange={(e) => handleMultiSelectFilter('degrees', degree._id, e.target.checked)}
                                                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                                        />
                                                        <span className="text-sm text-gray-700">{degree.name}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    {/* Specializations Filter */}
                                    <div className="mb-6">
                                        <label className="block text-sm font-medium text-gray-700 mb-3">Specializations</label>
                                        {specializationsLoading ? (
                                            <div className="flex items-center justify-center py-4">
                                                <LoadingSpinner size="sm" />
                                            </div>
                                        ) : specializationsError ? (
                                            <div className="text-red-500 text-sm">Failed to load specializations</div>
                                        ) : (
                                            <div className="max-h-48 overflow-y-auto space-y-2">
                                                {specializations.map((specialization) => (
                                                    <label key={specialization._id} className="flex items-center space-x-2 cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            checked={filters.specializations.includes(specialization._id)}
                                                            onChange={(e) => handleMultiSelectFilter('specializations', specialization._id, e.target.checked)}
                                                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                                        />
                                                        <span className="text-sm text-gray-700">{specialization.name}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    {/* Fee Range Filter */}
                                    <div className="mb-6">
                                        <label className="block text-sm font-medium text-gray-700 mb-3">Fee Range</label>
                                        <select
                                            value={filters.feeRange}
                                            onChange={(e) => handleFilterChange('feeRange', e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                        >
                                            <option value="">All Fee Ranges</option>
                                            <option value="below_2_lakhs">Below ₹2 Lakhs</option>
                                            <option value="above_2_lakhs">Above ₹2 Lakhs</option>
                                        </select>
                                    </div>


                                    {/* Approvals Filter */}
                                    <div className="mb-6">
                                        <label className="block text-sm font-medium text-gray-700 mb-3">Approvals</label>
                                        {approvalsLoading ? (
                                            <div className="flex items-center justify-center py-4">
                                                <LoadingSpinner size="sm" />
                                            </div>
                                        ) : approvalsError ? (
                                            <div className="text-red-500 text-sm">Failed to load approvals</div>
                                        ) : (
                                            <div className="max-h-48 overflow-y-auto space-y-2">
                                                {approvals.map((approval) => (
                                                    <label key={approval._id} className="flex items-center space-x-2 cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            checked={filters.approvals.includes(approval._id)}
                                                            onChange={(e) => handleMultiSelectFilter('approvals', approval._id, e.target.checked)}
                                                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                                        />
                                                        <span className="text-sm text-gray-700">{approval.name}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                </div>
                            </div>

                            {/* Main Content */}
                            <div className="flex-1">
                                {isLoading ? (
                                    <div className="flex justify-center py-12">
                                        <LoadingSpinner size="lg" />
                                    </div>
                                ) : (
                                    <>
                                        {/* Results Count */}
                                        <div className="mb-8">
                                            {universities.length > 0 ? (
                                                <p className="text-gray-600">
                                                    Showing {universities.length} of {pagination.total_count || 0} universities
                                                </p>
                                            ) : (
                                                <div className="text-center py-12">
                                                    <div className="text-gray-500 mb-4">
                                                        <AcademicCapIcon className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                                                        <h3 className="text-lg font-medium text-gray-900 mb-2">No universities found</h3>
                                                        <p className="text-gray-600 mb-4">
                                                            Try adjusting your filters or search terms to find more results.
                                                        </p>
                                                        <button
                                                            onClick={clearAllFilters}
                                                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                                                        >
                                                            Clear All Filters
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {/* Universities Grid */}
                                        {universities.length > 0 && (
                                            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
                                                {universities.map((university, index) => (
                                                    <motion.div
                                                        key={university._id}
                                                        initial={{ opacity: 0, y: 20 }}
                                                        animate={{ opacity: 1, y: 0 }}
                                                        transition={{ duration: 0.6, delay: index * 0.1 }}
                                                        className="bg-white rounded-2xl shadow-sm hover:shadow-xl border border-gray-100 hover:border-primary-100 transition-all duration-300 overflow-hidden flex flex-col h-full group relative"
                                                    >
                                                        {/* University Image Header */}
                                                        <div className="relative h-40 overflow-hidden shrink-0 border-b border-gray-100">
                                                            <UniversityImage university={university} />

                                                            {/* Featured Badge */}
                                                            {university.featured && (
                                                                <div className="absolute top-4 right-4 bg-accent-500 text-white rounded-full px-3 py-1 text-xs font-bold shadow-sm border border-accent-600">
                                                                    Featured
                                                                </div>
                                                            )}
                                                        </div>

                                                        {/* Content */}
                                                        <div className="p-6 flex flex-col flex-1">
                                                            {/* Title */}
                                                            <div className="mb-4">
                                                                <Link to={`/universities/${university.slug}`}>
                                                                    <h3 className="text-xl font-bold text-gray-900 leading-tight group-hover:text-primary-600 transition-colors duration-200 border-b border-transparent hover:border-primary-300 inline-block">
                                                                        {university.name}
                                                                    </h3>
                                                                </Link>
                                                            </div>

                                                            {/* Introduction / Summary */}
                                                            {university.introduction && (
                                                                <p className="text-gray-600 text-sm line-clamp-2 mb-5 leading-relaxed">
                                                                    {university.introduction.replace(/<[^>]*>/g, '').substring(0, 150)}...
                                                                </p>
                                                            )}

                                                            {/* Approval Tags */}
                                                            {university.approvals && university.approvals.length > 0 && (
                                                                <div className="mb-6">
                                                                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Recognitions</p>
                                                                    <div className="flex flex-wrap gap-1.5">
                                                                        {university.approvals.slice(0, 3).map((approval) => (
                                                                            <div
                                                                                key={approval._id || approval}
                                                                                className="inline-flex items-center space-x-1 bg-green-50 text-green-700 border border-green-200 px-2 py-1 rounded text-[11px] font-medium"
                                                                            >
                                                                                <CheckBadgeIcon className="w-3.5 h-3.5 shrink-0" />
                                                                                <span className="truncate max-w-[120px]">{approval.name || approval}</span>
                                                                            </div>
                                                                        ))}
                                                                        {university.approvals.length > 3 && (
                                                                            <div className="inline-flex items-center px-2 py-1 rounded bg-gray-50 text-gray-500 text-[11px] font-medium border border-gray-100">
                                                                                +{university.approvals.length - 3} more
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            )}

                                                            {/* Spacer to push fee and buttons to bottom */}
                                                            <div className="flex-1"></div>

                                                            {/* Divider */}
                                                            <div className="h-px w-full bg-gray-100 mb-5 mt-2"></div>

                                                            {/* Fee & Action Block */}
                                                            <div className="flex flex-col gap-4 mt-auto">
                                                                {/* Fee */}
                                                                <div className="flex justify-between items-center bg-gray-50 px-4 py-3 rounded-xl border border-gray-100">
                                                                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Avg. Fee Type</span>
                                                                    <div className="flex items-center text-gray-900 font-bold">
                                                                        <CurrencyRupeeIcon className="w-4 h-4 mr-1 text-gray-500" />
                                                                        <span>
                                                                            {university.fee_range?.type === 'below_2_lakhs' ? 'Below 2 Lakhs' :
                                                                                university.fee_range?.type === 'above_2_lakhs' ? 'Above 2 Lakhs' :
                                                                                    university.fee_range?.type || 'Contact for Details'}
                                                                        </span>
                                                                    </div>
                                                                </div>

                                                                {/* Action Buttons */}
                                                                <div className="grid grid-cols-2 gap-2">
                                                                    <button
                                                                        onClick={() => handleEnquiry(university, 'enquiry')}
                                                                        className="w-full bg-primary-600 text-white px-4 py-2.5 rounded-xl hover:bg-primary-700 transition-all font-semibold text-sm shadow-sm active:scale-[0.98]"
                                                                    >
                                                                        Enquiry Now
                                                                    </button>
                                                                    <div className="flex gap-2">
                                                                        <button
                                                                            onClick={() => handleEnquiry(university, 'brochure')}
                                                                            className="flex-1 bg-white text-gray-700 border border-gray-200 px-3 py-2.5 rounded-xl hover:bg-gray-50 transition-all font-semibold text-sm shadow-sm active:scale-[0.98] flex justify-center items-center"
                                                                            title="Download Brochure"
                                                                        >
                                                                            Brochure
                                                                        </button>
                                                                        <a
                                                                            href={`/universities/${university.slug}`}
                                                                            className="flex-shrink-0 bg-primary-50 text-primary-600 border border-primary-100 w-10 py-2.5 rounded-xl hover:bg-primary-100 transition-all font-semibold text-sm shadow-sm active:scale-[0.98] flex justify-center items-center group-hover:bg-primary-600 group-hover:text-white"
                                                                            title="View Details"
                                                                        >
                                                                            <ArrowRightIcon className="w-4 h-4" />
                                                                        </a>
                                                                    </div>
                                                                </div>
                                                                <button
                                                                    onClick={() => handleEnquiry(university, 'counselling')}
                                                                    className="w-full bg-primary-600 outline-none text-white px-4 py-3 rounded-xl hover:bg-primary-700 transition-all font-bold text-sm shadow-md active:scale-[0.98] flex justify-center items-center"
                                                                >
                                                                    Get Free Career Counselling
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </motion.div>
                                                ))}
                                            </div>
                                        )}

                                        {/* Pagination */}
                                        {pagination.total_pages > 1 && (
                                            <div className="mt-12 flex justify-center">
                                                <div className="flex items-center space-x-2">
                                                    <button
                                                        onClick={() => handlePageChange(pagination.current_page - 1)}
                                                        disabled={pagination.current_page === 1}
                                                        className="px-4 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        Previous
                                                    </button>

                                                    {Array.from({ length: pagination.total_pages }, (_, i) => i + 1).map((page) => (
                                                        <button
                                                            key={page}
                                                            onClick={() => handlePageChange(page)}
                                                            className={`px-4 py-2 text-sm font-medium rounded-lg ${page === pagination.current_page
                                                                ? 'bg-primary-600 text-white'
                                                                : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                                                                }`}
                                                        >
                                                            {page}
                                                        </button>
                                                    ))}

                                                    <button
                                                        onClick={() => handlePageChange(pagination.current_page + 1)}
                                                        disabled={pagination.current_page === pagination.total_pages}
                                                        className="px-4 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        Next
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </section>
            </div>

            {/* Enquiry Modal */}
            <EnquiryModal
                isOpen={enquiryModal.isOpen}
                onClose={handleCloseEnquiryModal}
                title={
                    enquiryModal.type === 'enquiry' ? 'Quick Enquiry' :
                        enquiryModal.type === 'brochure' ? 'Download Brochure' :
                            'Free Career Counselling'
                }
                source={enquiryModal.type}
                itemName={enquiryModal.university?.name || ''}
                itemType="university"
                itemId={enquiryModal.university?._id || null}
            />
        </>
    )
}

export default Universities
